import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Building2, User, ChevronRight, MapPin, CheckCircle2, UserCircle, AlertCircle, Clock, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RoleSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, isRTL } = useLanguage();
  const user = location.state?.user;
  
  // Check for employer invite from AuthPage
  const employerInviteCode = location.state?.employerInviteCode;
  const invitedCompanyName = location.state?.invitedCompanyName;
  
  const [selectedRole, setSelectedRole] = useState(employerInviteCode ? "employer" : null);
  const [companyName, setCompanyName] = useState("");
  const [payType, setPayType] = useState("hourly");
  const [inviteCode, setInviteCode] = useState(employerInviteCode || "");
  const [displayName, setDisplayName] = useState(user?.name || "");
  const [foundCompany, setFoundCompany] = useState(invitedCompanyName ? { name: invitedCompanyName } : null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [showDisplayNameDialog, setShowDisplayNameDialog] = useState(false);
  const [hasSeenDialog, setHasSeenDialog] = useState(!!employerInviteCode); // Don't show popup if already invited
  const [joinError, setJoinError] = useState(null);
  const [isEmployerInvite, setIsEmployerInvite] = useState(!!employerInviteCode);

  // Check if user already has a role and redirect them
  useEffect(() => {
    if (user?.role && user?.company_id) {
      if (user.role === "employer" || user.role === "admin") {
        navigate('/employer', { state: { user } });
      } else if (user.role === "employee") {
        navigate('/employee', { state: { user } });
      }
    }
  }, [user, navigate]);

  // Look up company when invite code changes
  useEffect(() => {
    const lookupCompany = async () => {
      if (inviteCode.length === 8) {
        setIsLookingUp(true);
        try {
          const response = await fetch(`${API_URL}/api/company/by-invite/${inviteCode}`, {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            setFoundCompany(data);
            // Show display name popup when company is found for first time
            if (!hasSeenDialog) {
              setShowDisplayNameDialog(true);
              setHasSeenDialog(true);
            }
          } else {
            setFoundCompany(null);
          }
        } catch (error) {
          setFoundCompany(null);
        } finally {
          setIsLookingUp(false);
        }
      } else {
        setFoundCompany(null);
      }
    };
    
    const debounce = setTimeout(lookupCompany, 300);
    return () => clearTimeout(debounce);
  }, [inviteCode, hasSeenDialog]);

  const handleDisplayNameConfirm = () => {
    if (!displayName.trim()) {
      toast.error("Please enter your display name");
      return;
    }
    setShowDisplayNameDialog(false);
  };

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    // For employer role: need company name OR invite code (for joining existing company)
    if (selectedRole === "employer" && !companyName.trim() && !isEmployerInvite) {
      toast.error("Please enter your company name");
      return;
    }

    if (selectedRole === "employee" && !inviteCode.trim()) {
      toast.error("Please enter the invite code from your employer");
      return;
    }

    if (selectedRole === "employee" && !displayName.trim()) {
      toast.error("Please enter your display name");
      return;
    }

    setIsLoading(true);
    setJoinError(null);

    try {
      // Use XMLHttpRequest to avoid fetch interception issues
      const responseData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/auth/setup-role`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.withCredentials = true;
        
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
          } catch (e) {
            resolve({ ok: false, status: xhr.status, data: { detail: 'Failed to parse response' } });
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error'));
        };
        
        // Build request body based on role and invite status
        const requestBody = {
          role: selectedRole,
          display_name: displayName || undefined
        };
        
        if (selectedRole === "employer") {
          if (isEmployerInvite && inviteCode) {
            // Joining existing company as employer
            requestBody.invite_code = inviteCode;
          } else {
            // Creating new company
            requestBody.company_name = companyName;
            requestBody.pay_type = payType;
          }
        } else if (selectedRole === "employee") {
          requestBody.invite_code = inviteCode;
        }
        
        xhr.send(JSON.stringify(requestBody));
      });

      if (!responseData.ok) {
        const errorMessage = responseData.data.detail || "Failed to setup role";
        setJoinError(errorMessage);
        toast.error(errorMessage, { duration: 8000 });
        return;
      }

      const updatedUser = responseData.data;
      toast.success("Setup complete!");

      if (selectedRole === "employer") {
        if (isEmployerInvite) {
          // Employer joining existing company goes directly to dashboard
          navigate('/employer', { state: { user: updatedUser } });
        } else {
          // New employers are redirected to subscription page to select a plan
          navigate('/subscription', { state: { user: updatedUser, isNewEmployer: true } });
        }
      } else {
        navigate('/employee', { state: { user: updatedUser } });
      }
    } catch (error) {
      console.error('Setup error:', error);
      const errorMsg = error.message || "An unexpected error occurred";
      setJoinError(errorMsg);
      toast.error(errorMsg, { duration: 8000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="w-full max-w-2xl">
        {/* Language Selector */}
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>

        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <MapPin className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-2xl text-foreground">{t("appName")}</span>
          </div>
          <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
            {t("welcomeTitle")}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-muted-foreground">
            {t("roleQuestion")}
          </p>
        </div>

        {/* Role Selection */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card 
            data-testid="role-employer-card"
            className={`cursor-pointer transition-all duration-300 hover:shadow-card-hover ${
              selectedRole === "employer" 
                ? "border-primary shadow-card-hover ring-2 ring-primary/20" 
                : "border-border hover:border-primary/30"
            }`}
            onClick={() => setSelectedRole("employer")}
          >
            <CardContent className="p-6">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-colors ${
                selectedRole === "employer" ? "bg-primary" : "bg-accent"
              }`}>
                <Building2 className={`w-7 h-7 ${
                  selectedRole === "employer" ? "text-primary-foreground" : "text-primary"
                }`} />
              </div>
              <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
                {t("imEmployer")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t("employerDesc")}
              </p>
            </CardContent>
          </Card>

          <Card 
            data-testid="role-employee-card"
            className={`cursor-pointer transition-all duration-300 hover:shadow-card-hover ${
              selectedRole === "employee" 
                ? "border-primary shadow-card-hover ring-2 ring-primary/20" 
                : "border-border hover:border-primary/30"
            }`}
            onClick={() => setSelectedRole("employee")}
          >
            <CardContent className="p-6">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-colors ${
                selectedRole === "employee" ? "bg-primary" : "bg-accent"
              }`}>
                <User className={`w-7 h-7 ${
                  selectedRole === "employee" ? "text-primary-foreground" : "text-primary"
                }`} />
              </div>
              <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
                {t("imEmployee")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t("employeeDesc")}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Form Fields */}
        {selectedRole && (
          <Card className="border-border shadow-card animate-slide-up">
            <CardHeader>
              <CardTitle className="font-heading">
                {selectedRole === "employer" 
                  ? (isEmployerInvite ? (t("joinAsEmployer") || "Join as Employer") : t("createCompany")) 
                  : t("joinCompany")}
              </CardTitle>
              <CardDescription>
                {selectedRole === "employer" 
                  ? (isEmployerInvite 
                      ? (t("joinAsEmployerDesc") || `You've been invited to join ${foundCompany?.name || 'a company'} as an employer.`)
                      : "Enter your company name to get started. You'll receive an invite code to share with employees.")
                  : "Enter the invite code provided by your employer."
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedRole === "employer" ? (
                <>
                  {isEmployerInvite ? (
                    /* Employer joining existing company */
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-6 h-6 text-blue-600" />
                          <div>
                            <p className="font-medium text-blue-800">{t("joiningCompany") || "Joining"}: <strong>{foundCompany?.name}</strong></p>
                            <p className="text-sm text-blue-600">{t("asEmployer") || "as an employer"}</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="display-name">{t("displayNameLabel")}</Label>
                        <Input
                          id="display-name"
                          data-testid="employer-display-name-input"
                          placeholder={t("displayNamePlaceholder")}
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          className="h-12"
                        />
                        <p className="text-xs text-muted-foreground">{t("displayNameHelp")}</p>
                      </div>
                    </div>
                  ) : (
                    /* Creating new company */
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="company-name">{t("companyNameLabel")}</Label>
                        <Input
                          id="company-name"
                          data-testid="company-name-input"
                          placeholder={t("companyNamePlaceholder")}
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          className="h-12"
                        />
                      </div>
                      <div className="space-y-3">
                        <Label>{t("payTypeLabel") || "Employee Pay Type"}</Label>
                        <RadioGroup value={payType} onValueChange={setPayType} className="grid grid-cols-2 gap-4">
                          <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${payType === "hourly" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`} onClick={() => setPayType("hourly")}>
                            <RadioGroupItem value="hourly" id="hourly" />
                            <div className="flex items-center gap-2">
                              <Clock className="w-5 h-5 text-primary" />
                              <div>
                                <Label htmlFor="hourly" className="font-medium cursor-pointer">{t("hourly") || "Hourly"}</Label>
                                <p className="text-xs text-muted-foreground">{t("hourlyDesc") || "Flexible shift-based scheduling"}</p>
                              </div>
                            </div>
                          </div>
                          <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${payType === "salary" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`} onClick={() => setPayType("salary")}>
                            <RadioGroupItem value="salary" id="salary" />
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-5 h-5 text-primary" />
                              <div>
                                <Label htmlFor="salary" className="font-medium cursor-pointer">{t("salary") || "Salary"}</Label>
                                <p className="text-xs text-muted-foreground">{t("salaryDesc") || "Fixed weekly schedules"}</p>
                              </div>
                            </div>
                          </div>
                        </RadioGroup>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="invite-code">{t("inviteCodeLabel")}</Label>
                    <Input
                      id="invite-code"
                      data-testid="invite-code-input"
                      placeholder={t("inviteCodePlaceholder")}
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      className="h-12 font-mono uppercase"
                      maxLength={8}
                    />
                    {isLookingUp && (
                      <p className="text-sm text-muted-foreground">Looking up company...</p>
                    )}
                    {foundCompany && (
                      <div className="flex items-center gap-2 p-3 bg-accent rounded-lg">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span className="text-sm font-medium text-foreground">
                          {t("joiningCompany")}: <strong>{foundCompany.name}</strong>
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {foundCompany && (
                    <div className="space-y-2">
                      <Label htmlFor="display-name">{t("displayNameLabel")}</Label>
                      <Input
                        id="display-name"
                        data-testid="display-name-input"
                        placeholder={t("displayNamePlaceholder")}
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="h-12"
                      />
                      <p className="text-xs text-muted-foreground">{t("displayNameHelp")}</p>
                    </div>
                  )}
                </>
              )}

              <Button
                onClick={handleSubmit}
                disabled={isLoading || (selectedRole === "employee" && !foundCompany)}
                data-testid="setup-submit-btn"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-full font-medium"
              >
                {isLoading ? t("loading") : t("continue")}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              
              {/* Error Message Display */}
              {joinError && (
                <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm flex items-start gap-3" data-testid="join-error-message">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <p className="font-medium">{joinError}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Display Name Popup Dialog */}
        <Dialog open={showDisplayNameDialog} onOpenChange={setShowDisplayNameDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                  <UserCircle className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <DialogTitle className="text-xl">{t("welcomeTitle")}!</DialogTitle>
                  <DialogDescription>
                    {t("joiningCompany")}: <strong className="text-primary">{foundCompany?.name}</strong>
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="popup-display-name" className="text-base font-medium">
                  {t("displayNameLabel")}
                </Label>
                <Input
                  id="popup-display-name"
                  data-testid="popup-display-name-input"
                  placeholder={t("displayNamePlaceholder")}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="h-12 text-base"
                  autoFocus
                />
                <p className="text-sm text-muted-foreground">{t("displayNameHelp")}</p>
              </div>
            </div>
            <DialogFooter>
              <Button 
                onClick={handleDisplayNameConfirm} 
                className="w-full h-12 rounded-full"
                data-testid="confirm-display-name-btn"
              >
                {t("continue")}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default RoleSetup;
